import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import StageBadge from "@/components/StageBadge";
import StageProgress from "@/components/StageProgress";
import ItemHistoryDialog from "@/components/order/ItemHistoryDialog";
import PaintStatusPill from "@/components/order/PaintStatusPill";
import ItemRevisionRibbon from "@/components/order/ItemRevisionRibbon";
import ItemColorLine from "@/components/order/ItemColorLine";
import { STEPS } from "@/lib/stages";
import { unitsAtPainter } from "@/lib/paintApi";
import {
  CaretRight, XCircle, ArrowsClockwise, WarningOctagon, ClockCounterClockwise,
} from "@phosphor-icons/react";

/**
 * Single line item row inside the order detail table.
 * Encapsulates display + per-row actions (advance / qa-fail / cancel / restore).
 */
export default function LineItemRow({ item, order, busy, onAdvance, onAskReason, onUncancel }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const isCancelled = item.cancelled;
  const currentIdx = STEPS.indexOf(item.current_step);
  const nextStep = !isCancelled && currentIdx >= 0 && currentIdx < STEPS.length - 1
    ? STEPS[currentIdx + 1]
    : null;
  // Ship-block: matches the mobile scanner rule — cannot advance to Shipped
  // while units are still at the painter.
  const paintBlocked = nextStep === "Shipped" && unitsAtPainter(item) > 0;

  return (
    <div className="px-5 py-4" data-testid={`item-row-${item.sku}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
        <ItemInfo item={item} order={order} />
        <div className="md:col-span-6">
          {isCancelled ? <CancelledNote item={item} /> : <StageProgress item={item} />}
          {!isCancelled && <PaintStatusPill item={item} className="mt-2" />}
        </div>
        <div className="md:col-span-3 flex flex-wrap items-center justify-end gap-2">
          <StageBadge step={item.current_step} cancelled={isCancelled} size="sm" />
          {!isCancelled && nextStep && onAdvance && !paintBlocked && (
            <AdvanceButton item={item} nextStep={nextStep} busy={busy} onAdvance={onAdvance} />
          )}
          {!isCancelled && paintBlocked && (
            <span
              className="text-[10px] uppercase tracking-[0.15em] font-bold rounded-md px-2 py-1 border border-purple-300 bg-purple-50 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800"
              data-testid={`paint-blocked-${item.sku}`}
              title="Cannot ship until all units return from paint"
            >
              Ship-blocked · paint
            </span>
          )}
          {!isCancelled && item.current_step === "QA" && onAskReason && (
            <QAFailButton item={item} onAskReason={onAskReason} />
          )}
          <HistoryButton item={item} onOpen={() => setHistoryOpen(true)} />
          {!isCancelled && onAskReason ? (
            <CancelIconButton item={item} onAskReason={onAskReason} />
          ) : isCancelled && onUncancel ? (
            <RestoreButton item={item} busy={busy} onUncancel={onUncancel} />
          ) : null}
        </div>
      </div>
      <ItemHistoryDialog item={item} open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </div>
  );
}

function ItemInfo({ item, order }) {
  return (
    <div className="md:col-span-3">
      <div className="font-mono font-semibold text-sm">{item.sku}</div>
      <div className="font-semibold text-zinc-900 truncate">{item.name}</div>
      <div className="text-xs text-zinc-500 mt-0.5 space-y-0.5">
        {item.category && (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="truncate">{item.category}</span>
            {(item.color || item.sku) && (
              <>
                <span aria-hidden>·</span>
                <ItemColorLine item={item} className="text-zinc-600 dark:text-zinc-300" />
              </>
            )}
          </div>
        )}
        {item.modifications && (
          <div className="italic">{item.modifications}</div>
        )}
        <div className="font-mono">×{item.units_total}</div>
      </div>
      <div className="mt-1.5">
        <ItemRevisionRibbon item={item} order={order} />
      </div>
    </div>
  );
}

function CancelledNote({ item }) {
  return (
    <div className="text-sm">
      <StageBadge step={item.current_step} cancelled size="sm" />
      {item.cancel_reason && (
        <div className="mt-1.5 text-xs text-red-700 italic">
          "{item.cancel_reason}" — {item.cancelled_by_name}
        </div>
      )}
    </div>
  );
}

function AdvanceButton({ item, nextStep, busy, onAdvance }) {
  return (
    <Button
      size="sm"
      onClick={() => onAdvance(item)}
      disabled={busy === item.id}
      className="bg-zinc-900 hover:bg-zinc-700 h-8"
      data-testid={`advance-${item.sku}`}
    >
      {busy === item.id ? (
        <ArrowsClockwise size={14} className="animate-spin" />
      ) : (
        <><CaretRight size={14} weight="bold" /> {nextStep}</>
      )}
    </Button>
  );
}

function QAFailButton({ item, onAskReason }) {
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => onAskReason({ item, action: "qa-fail" })}
      className="h-8 border-red-300 text-red-700 hover:bg-red-50"
      data-testid={`qa-fail-${item.sku}`}
    >
      <WarningOctagon size={14} weight="bold" /> QA Fail
    </Button>
  );
}

function CancelIconButton({ item, onAskReason }) {
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => onAskReason({ item, action: "cancel" })}
      className="h-8 text-zinc-500 hover:text-red-700"
      data-testid={`cancel-${item.sku}`}
    >
      <XCircle size={14} />
    </Button>
  );
}

function RestoreButton({ item, busy, onUncancel }) {
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => onUncancel(item)}
      disabled={busy === item.id}
      className="h-8"
      data-testid={`uncancel-${item.sku}`}
    >
      Restore
    </Button>
  );
}

function HistoryButton({ item, onOpen }) {
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={onOpen}
      className="h-8 text-zinc-500 hover:text-zinc-900"
      title="View history"
      data-testid={`history-${item.sku}`}
    >
      <ClockCounterClockwise size={14} weight="bold" />
    </Button>
  );
}
